import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, BarChart3, MapPin, Building2, Layers, Archive, Activity, XCircle,
  FileEdit, Trophy, Handshake, Percent, Users, Ban, Download, LifeBuoy,
  BookOpen, Mail, X,
} from 'lucide-react';

/**
 * Vertical primary navigation for the public homepage.
 *
 * Renders as a permanent left column at `lg` and above, and as a slide-in
 * drawer on smaller viewports (controlled by `open` / `onClose`). Uses the
 * navy + gold brand palette. `Coming soon` items surface a small pill so
 * visitors are not confused when a route hasn't shipped yet — they do NOT
 * navigate anywhere.
 *
 * Reuses existing routes wherever they already exist:
 *   • `/`                          — Home / Concessions (anchor)
 *   • `/tenders/public-search`     — tender search variants
 *   • `/vendors`                   — Status of Vendors
 *   • `/privacy-policy`            — Guidelines & Policies
 *
 * Hash targets (`#hero`, `#for-suppliers`, `#contact`) resolve to sections
 * already present in `LandingPage.js`.
 */
const ITEMS = [
  { key: 'home',                  label: 'Home',                      icon: Home,        to: '/#hero' },
  { key: 'mis',                   label: 'MIS Reports',               icon: BarChart3,   soon: true },
  { key: 'tenders-location',      label: 'Tenders by Location',       icon: MapPin,      to: '/tenders/public-search?group=location' },
  { key: 'tenders-organisation',  label: 'Tenders by Organisation',   icon: Building2,   to: '/tenders/public-search?group=organisation' },
  { key: 'tenders-classification',label: 'Tenders by Classification', icon: Layers,      to: '/tenders/public-search?group=category' },
  { key: 'tenders-archive',       label: 'Tenders in Archive',        icon: Archive,     to: '/tenders/public-search?status=closed' },
  { key: 'tenders-status',        label: 'Tenders Status',            icon: Activity,    to: '/#tenders-status' },
  { key: 'cancelled',             label: 'Cancelled / Retendered',    icon: XCircle,     to: '/tenders/public-search?status=cancelled' },
  { key: 'corrigendum',           label: 'Corrigendum',               icon: FileEdit,    soon: true },
  { key: 'awards',                label: 'Bid Awards',                icon: Trophy,      to: '/tenders/public-search?status=awarded' },
  { key: 'rate-contract',         label: 'Rate Contract',             icon: Handshake,   soon: true },
  { key: 'concessions',           label: 'Concessions',               icon: Percent,     to: '/#founding-offer' },
  { key: 'vendors',               label: 'Status of Vendors',         icon: Users,       to: '/vendors' },
  { key: 'debarment',             label: 'Debarment List',            icon: Ban,         soon: true },
  { key: 'downloads',             label: 'Downloads',                 icon: Download,    to: '/#downloads' },
  { key: 'help-contractors',      label: 'Help For Contractors',      icon: LifeBuoy,    to: '/#for-suppliers' },
  { key: 'guidelines',            label: 'Guidelines & Policies',     icon: BookOpen,    to: '/privacy-policy' },
  { key: 'contact',               label: 'Contact Us',                icon: Mail,        to: '/#contact' },
];

export default function PublicSidebar({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (item) => {
    if (item.soon) return;
    onClose?.();
    if (item.to?.startsWith('/#')) {
      // Anchor within landing page — navigate then smooth-scroll after render.
      const hash = item.to.slice(1);
      if (location.pathname !== '/') {
        navigate('/' + hash);
      } else {
        const id = hash.slice(1);
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
      return;
    }
    navigate(item.to);
  };

  const isActive = (item) => {
    if (item.soon) return false;
    if (item.to === '/#hero') {
      return location.pathname === '/' && (location.hash === '' || location.hash === '#hero');
    }
    if (item.to?.startsWith('/#')) {
      return location.pathname === '/' && location.hash === item.to.slice(1);
    }
    return location.pathname === item.to?.split('?')[0];
  };

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          data-testid="sidebar-backdrop"
          aria-hidden="true"
        />
      )}

      <aside
        className={
          'bg-[#0A1F44] text-white shrink-0 ' +
          // Desktop: static column, own scroll
          'lg:sticky lg:top-[76px] lg:h-[calc(100vh-76px)] lg:w-64 lg:block lg:overflow-y-auto ' +
          // Mobile: drawer sliding from the left
          'fixed inset-y-0 left-0 z-50 w-72 overflow-y-auto transform transition-transform duration-200 ease-out lg:transform-none ' +
          (open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0')
        }
        aria-label="Primary navigation"
        data-testid="public-sidebar"
      >
        {/* Mobile close button */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-white/10">
          <span className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider">Menu</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-white/10"
            data-testid="sidebar-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="py-3">
          <ul className="space-y-0.5">
            {ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => handleClick(item)}
                    disabled={item.soon}
                    aria-current={active ? 'page' : undefined}
                    className={
                      'w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm border-l-4 transition-colors ' +
                      (active
                        ? 'bg-white/10 border-[#D4AF37] text-white font-semibold'
                        : 'border-transparent text-slate-200 hover:bg-white/5 hover:border-white/20 hover:text-white ') +
                      (item.soon ? ' opacity-60 cursor-not-allowed' : '')
                    }
                    data-testid={`sidebar-item-${item.key}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="flex-1 leading-tight">{item.label}</span>
                    {item.soon && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30" data-testid={`sidebar-soon-${item.key}`}>
                        Soon
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
}
